// views/Profile/components/OrgBalanceCard.jsx
import React from 'react';
import { Users, RefreshCw, AlertCircle } from 'lucide-react';
import './OrgBalanceCard.css';

const formatCoins = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const computeUsedPercent = ({ allowance, periodBalance }) => {
  if (!allowance || allowance <= 0) return null;
  const used = allowance - (periodBalance ?? 0);
  return Math.max(0, Math.min(100, (used / allowance) * 100));
};

/**
 * OrgBalanceCard — Enterprise pool view (admin / owner only).
 *
 * Mirrors BalanceCard's account variant in spirit but operates on
 * the org-level pool: total/period/extra balances, the configured
 * `customMonthlyCoinAllowance`, an allowance progress bar and a
 * "Resets in N days" hint.
 *
 * Only fed when `useOrgBalance` succeeds; the EmptyState for
 * NOT_AN_ENTERPRISE_ORG is handled by the parent. Other errors are
 * surfaced inline.
 */
export default function OrgBalanceCard({
  totalBalance,
  periodBalance,
  extraBalance,
  customMonthlyCoinAllowance,
  lastResetAt,
  organizationName,
  loading = false,
  refreshing = false,
  error = null,
  onRefresh,
}) {
  if (loading) {
    return (
      <section className="org-balance org-balance--loading" aria-busy="true">
        <div className="org-balance__header">
          <span className="org-balance__label">
            <Users size={16} aria-hidden="true" />
            Pool balance
          </span>
        </div>
        <div className="org-balance__hero org-balance__hero--skeleton" />
        <div className="org-balance__chips">
          <span className="org-balance__chip org-balance__chip--skeleton" />
          <span className="org-balance__chip org-balance__chip--skeleton" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="org-balance org-balance--error">
        <div className="org-balance__header">
          <span className="org-balance__label">
            <Users size={16} aria-hidden="true" />
            Pool balance
          </span>
        </div>
        <div className="org-balance__error">
          <AlertCircle size={20} aria-hidden="true" />
          <p>{error.message || 'Unable to load pool balance'}</p>
          {error.requestId && (
            <small className="org-balance__request-id">
              ref: {error.requestId}
            </small>
          )}
          {onRefresh && (
            <button
              type="button"
              className="org-balance__retry"
              onClick={onRefresh}
            >
              Retry
            </button>
          )}
        </div>
      </section>
    );
  }

  const usedPercent = computeUsedPercent({
    allowance: customMonthlyCoinAllowance,
    periodBalance,
  });
  const lastResetLabel = formatDate(lastResetAt);

  return (
    <section className="org-balance">
      <header className="org-balance__header">
        <span className="org-balance__label">
          <Users size={16} aria-hidden="true" />
          Pool balance
          {organizationName && (
            <span className="org-balance__org-name">
              · {organizationName}
            </span>
          )}
        </span>
        {onRefresh && (
          <button
            type="button"
            className={`org-balance__icon-btn${
              refreshing ? ' org-balance__icon-btn--spinning' : ''
            }`}
            onClick={onRefresh}
            aria-label="Refresh pool balance"
            disabled={refreshing}
          >
            <RefreshCw size={16} aria-hidden="true" />
          </button>
        )}
      </header>

      <div className="org-balance__hero">
        <span className="org-balance__hero-number">
          {formatCoins(totalBalance)}
        </span>
        <span className="org-balance__hero-suffix">Zenit Coins</span>
      </div>

      <ul className="org-balance__chips" aria-label="Pool breakdown">
        <li className="org-balance__chip">
          <span className="org-balance__chip-label">Period</span>
          <span className="org-balance__chip-value">
            {formatCoins(periodBalance)}
          </span>
        </li>
        <li className="org-balance__chip">
          <span className="org-balance__chip-label">Extra</span>
          <span className="org-balance__chip-value">
            {formatCoins(extraBalance)}
          </span>
        </li>
        {customMonthlyCoinAllowance != null && (
          <li className="org-balance__chip">
            <span className="org-balance__chip-label">Allowance</span>
            <span className="org-balance__chip-value">
              {formatCoins(customMonthlyCoinAllowance)}
            </span>
          </li>
        )}
      </ul>

      {usedPercent != null && (
        <div className="org-balance__progress">
          <div className="org-balance__progress-track">
            <div
              className="org-balance__progress-fill"
              style={{ width: `${usedPercent.toFixed(1)}%` }}
              role="progressbar"
              aria-valuenow={Math.round(usedPercent)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Pool allowance used"
            />
          </div>
          <span className="org-balance__progress-text">
            {Math.round(usedPercent)}% of{' '}
            {formatCoins(customMonthlyCoinAllowance)} used this period
          </span>
        </div>
      )}

      {lastResetLabel && (
        <p className="org-balance__hint">Last reset on {lastResetLabel}</p>
      )}
    </section>
  );
}
